import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPublicCampaign, submitPublicLead } from '../utils/api';
import { getFontPair } from '../constants/fontPairs';
import { Phone, MapPin, ShieldCheck, BadgeCheck, CreditCard, Clock, CheckCircle2 } from 'lucide-react';

const TRADE_HERO_IMAGES = {
  HVAC: 'https://static.prod-images.emergentagent.com/jobs/6c107657-a40e-4170-a322-d7aa39016c78/images/2991f2ee65e85f771c11ca144510d14fa63ed3ab5c3f74c89cb673d2c3b4bd04.jpeg',
  Plumbing: 'https://static.prod-images.emergentagent.com/jobs/6c107657-a40e-4170-a322-d7aa39016c78/images/cd00871080d3dfe55c07fc997f864679950f9950b55980a7b3effb0d750493bc.jpeg',
  Electrical: 'https://static.prod-images.emergentagent.com/jobs/6c107657-a40e-4170-a322-d7aa39016c78/images/20f8e940d37fc1de12c3f192278fc7e3d53a2f9082941db94ac78df9734e6da0.jpeg',
};

const LeadForm = ({ campaignId, ownerParam, accent, heading, disabled }) => {
  const [form, setForm] = useState({ name: '', phone: '', issue: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) {
      setError('This page is in preview mode — leads are disabled until the business profile is completed.');
      return;
    }
    if (!form.name.trim() || !form.phone.trim()) {
      setError('Please enter your name and phone number.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await submitPublicLead(campaignId, { ...form, u: ownerParam || undefined });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'lead_form_submit', campaign_id: campaignId });
      setDone(true);
    } catch {
      setError('Something went wrong. Please call us instead.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="lead-form-success">
        <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: accent }} />
        <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: heading }}>Request Received!</h3>
        <p className="text-gray-300">We'll call you back shortly to get you scheduled.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      <input
        type="text"
        placeholder="Your Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        data-testid="lead-form-name"
        className="w-full px-4 py-3.5 bg-black/40 border border-white/15 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white/40"
      />
      <input
        type="tel"
        placeholder="Phone Number"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        data-testid="lead-form-phone"
        className="w-full px-4 py-3.5 bg-black/40 border border-white/15 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white/40"
      />
      <textarea
        placeholder="Briefly describe the issue (optional)"
        value={form.issue}
        onChange={(e) => setForm({ ...form, issue: e.target.value })}
        rows={3}
        data-testid="lead-form-issue"
        className="w-full px-4 py-3.5 bg-black/40 border border-white/15 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white/40 resize-none"
      />
      {error && <p className="text-red-400 text-sm" data-testid="lead-form-error">{error}</p>}
      <button
        type="submit"
        disabled={submitting}
        data-testid="lead-form-submit"
        className="w-full py-4 rounded-lg font-bold text-lg text-black transition-transform hover:scale-[1.02]"
        style={{ backgroundColor: accent }}
      >
        {submitting ? 'Sending...' : 'Request My Free Quote'}
      </button>
      <p className="text-xs text-gray-400 text-center">Takes 30 seconds. No obligation — we'll call you back fast.</p>
    </form>
  );
};

const PublicLandingPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const ownerParam = searchParams.get('u') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicCampaign(id, ownerParam)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id, ownerParam]);

  // Google Tag Manager: dataLayer always available; container loads if the contractor set one
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    const gtmId = data?.company?.gtm_container_id;
    if (gtmId && !document.getElementById('sgv-gtm')) {
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const s = document.createElement('script');
      s.id = 'sgv-gtm';
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
      document.head.appendChild(s);
    }
  }, [data]);

  const trackCall = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'call_click', campaign_id: id });
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/campaign/${id}/call-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ u: ownerParam }),
    }).catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f2436]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!data || !data.campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f2436]">
        <div className="card max-w-md text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Campaign Not Found</h1>
          <p className="text-light-gray">This campaign may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  const { campaign, company } = data;
  const font = getFontPair(company?.font_pair);
  const accent = company?.brand_color_primary && company.brand_color_primary !== '#CCCCCC'
    ? company.brand_color_primary : '#E8731A';
  const companyName = company?.name || 'Elite Home Services';
  const phone = company?.phone || '(555) 123-4567';
  const city = company?.city || company?.service_areas?.[0] || 'your area';
  const service = campaign.primary_service || `${campaign.trade} service`;
  const heroImg = TRADE_HERO_IMAGES[campaign.trade] || TRADE_HERO_IMAGES.HVAC;
  const years = company?.years_in_business;
  const compliment = (company?.customer_compliments || '').trim();
  const financingText = (company?.financing_offer || '').trim() || 'Flexible financing available';

  // Go-live gate: phone, city, and license must be REAL profile data (no placeholders)
  const hasRealPhone = !!company?.phone && !company.phone.includes('555');
  const hasRealCity = !!(company?.city || (company?.service_areas || []).length);
  const hasRealLicense = !!company?.license_number;
  const isPreview = !(hasRealPhone && hasRealCity && hasRealLicense);

  const scrollToForm = (e) => {
    e.preventDefault();
    document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const headingStyle = { fontFamily: font.heading, fontWeight: font.headingWeight };

  return (
    <div className="min-h-screen bg-[#0f2436] text-white" style={{ fontFamily: font.body }} data-testid="public-landing-page">
      {isPreview && (
        <div className="relative z-30 bg-yellow-500 text-black text-center text-sm font-bold px-4 py-2" data-testid="landing-preview-banner">
          PREVIEW MODE — this page can't take live leads yet. Add your real phone{!hasRealCity ? ', city' : ''}{!hasRealLicense ? ', license #' : ''} in My Brand to go live.
        </div>
      )}
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/70 to-transparent">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="landing-header-brand">
            {company?.logo_url ? (
              <img src={company.logo_url} alt={`${companyName} logo`} className="h-12 max-w-[200px] object-contain" data-testid="landing-logo" />
            ) : (
              <span className="text-2xl font-bold" style={headingStyle}>{companyName}</span>
            )}
          </div>
          <a
            href={`tel:${phone}`}
            onClick={trackCall}
            data-testid="landing-header-call-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-black transition-transform hover:scale-105"
            style={{ backgroundColor: accent }}
          >
            <Phone size={18} />
            {phone}
          </a>
        </div>
      </header>

      {/* Hero — the one dominant moment */}
      <section className="relative min-h-[560px] flex items-center" data-testid="landing-hero">
        <div className="absolute inset-0">
          <img src={heroImg} alt={`${campaign.trade} technician at work`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2436] via-[#0f2436]/85 to-[#0f2436]/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f2436] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl leading-[1.05] mb-5" style={headingStyle} data-testid="landing-headline">
              {campaign.title || campaign.campaign_name}
            </h1>
            <p className="text-xl text-gray-200 mb-6">
              Fast, professional {service.toLowerCase()} in {city} — done right the first time by {companyName}.
            </p>
            {company?.default_offer && (
              <div className="inline-block border px-5 py-2.5 rounded-lg mb-8" style={{ borderColor: accent, backgroundColor: `${accent}1A` }}>
                <p className="font-bold" style={{ color: accent }} data-testid="landing-offer">{company.default_offer}</p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4">
              <a
                href={`tel:${phone}`}
                onClick={trackCall}
                data-testid="landing-hero-call-btn"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold text-lg text-black transition-transform hover:scale-105"
                style={{ backgroundColor: accent }}
              >
                <Phone size={22} />
                Call Now — We Answer Fast
              </a>
              <a
                href="#quote-form"
                onClick={scrollToForm}
                data-testid="landing-hero-quote-btn"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold text-lg border border-white/30 hover:border-white/60 transition-colors"
              >
                Get a Free Quote →
              </a>
            </div>
            <p className="text-sm text-gray-400 mt-3">Calling is fastest for same-day service. The quote form takes 30 seconds.</p>
          </div>
        </div>
      </section>

      {/* Trust badge strip */}
      <section className="border-y border-white/10 bg-black/40" data-testid="landing-trust-badges">
        <div className="max-w-6xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2.5">
            <ShieldCheck size={22} style={{ color: accent }} />
            <span className="text-gray-200">Licensed &amp; Insured{company?.license_number ? ` · Lic. #${company.license_number}` : ''}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <BadgeCheck size={22} style={{ color: accent }} />
            <span className="text-gray-200">{years ? `${years} Years in Business` : 'Locally Owned & Operated'}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Clock size={22} style={{ color: accent }} />
            <span className="text-gray-200">Same-Day &amp; Next-Day Appointments</span>
          </div>
          <div className="flex items-center gap-2.5">
            <CreditCard size={22} style={{ color: accent }} />
            <span className="text-gray-200" data-testid="landing-financing">{financingText}</span>
          </div>
        </div>
      </section>

      {/* Benefit copy — rewritten from internal fields */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12 items-start" data-testid="landing-problem-section">
        <div>
          <h2 className="text-3xl mb-5" style={headingStyle}>Sound familiar?</h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            {campaign.homeowner_symptoms && campaign.problem && campaign.pain_point
              ? <>{campaign.homeowner_symptoms}? That usually means {String(campaign.problem).toLowerCase()} — and{' '}
                {String(campaign.pain_point).toLowerCase()} isn't something you should have to put up with.</>
              : <>If something's not right with your {campaign.trade.toLowerCase()} system, you shouldn't have to put up with it.</>}
          </p>
          <p className="text-gray-300 text-lg leading-relaxed">
            The longer you wait, the worse (and more expensive) it tends to get. Our {campaign.trade.toLowerCase()} team
            diagnoses the real cause, gives you an upfront price, and gets it fixed — usually the same day.
          </p>
        </div>
        <div className="space-y-4">
          {[
            { icon: Clock, title: 'Fast Scheduling', text: 'Same-day and next-day appointments — we know this can\u2019t wait.' },
            { icon: ShieldCheck, title: 'Upfront, Honest Pricing', text: 'You approve the exact price before any work begins. No surprises.' },
            { icon: BadgeCheck, title: 'Fixed Right the First Time', text: `Experienced, background-checked ${campaign.trade.toLowerCase()} pros${company?.warranty_language ? ` — ${company.warranty_language}` : ', with our workmanship guarantee'}.` },
          ].map((b, i) => (
            <div key={i} className="flex gap-4 p-5 rounded-xl bg-white/[0.04] border border-white/10">
              <b.icon size={26} className="shrink-0 mt-0.5" style={{ color: accent }} />
              <div>
                <h3 className="font-bold text-white mb-1">{b.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What customers say — only real, owner-provided feedback (no fabricated reviews) */}
      {compliment && (
        <section className="bg-black/40 border-y border-white/10" data-testid="landing-reviews">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <h2 className="text-3xl mb-2" style={headingStyle}>What our customers tell us</h2>
            <p className="text-gray-300 text-xl leading-relaxed italic max-w-3xl">
              "{compliment}"
            </p>
            <p className="text-gray-400 text-sm mt-4">— what customers tell us most about {companyName}.</p>
          </div>
        </section>
      )}

      {/* Lead form + final CTA */}
      <section id="quote-form" className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-12 items-center" data-testid="landing-cta-section">
        <div className="md:col-span-2">
          <h2 className="text-3xl mb-4" style={headingStyle}>Get your free quote</h2>
          <p className="text-gray-300 leading-relaxed mb-6">
            Tell us what's going on and we'll call you back with straight answers and an honest price for {service.toLowerCase()} in {city}.
          </p>
          <div className="flex items-center gap-3 text-gray-300">
            <Phone size={20} style={{ color: accent }} />
            <span>Need it handled <span className="font-bold text-white">today</span>? Call{' '}
              <a href={`tel:${phone}`} onClick={trackCall} className="font-bold underline decoration-2" style={{ color: accent }} data-testid="landing-footer-phone">{phone}</a>
            </span>
          </div>
          {company?.service_areas && company.service_areas.length > 0 && (
            <div className="flex items-start gap-2 text-gray-400 text-sm mt-5">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <p>Serving {company.service_areas.join(', ')}</p>
            </div>
          )}
        </div>
        <div className="md:col-span-3 p-8 rounded-2xl bg-white/[0.04] border border-white/10">
          <LeadForm campaignId={id} ownerParam={ownerParam} accent={accent} heading={font.heading} disabled={isPreview} />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/60 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-8 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} {companyName}. All rights reserved.</p>
          <p className="mt-2">
            Licensed &amp; Insured{company?.license_number ? ` · Lic. #${company.license_number}` : ''}
            {years ? ` · Serving ${city} for ${years} years` : ''}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicLandingPage;
